import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import { useGetFeedPlayersQuery } from '@/api/feed/getFeedPlayers';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Skeleton } from '@/components/ui/skeleton';
import { TeamInformationSection } from '@/components/forms/team/TeamInformationSection';
import { TeamStatsSection } from '@/components/forms/team/TeamStatsSection';
import { TeamPlayersSection } from '@/components/forms/team/TeamPlayersSection';
import { getTeamLogoUrl } from '@/utils/teamLogo';
import { getTeamAvatarUrl, type Sport } from '@/utils/jersey-mapping';
import { isSuperAdmin } from '@/utils/auth';

interface PlayerData {
  id: string;
  teamId: string;
  name: string;
  position: string;
  jerseyNumber?: string;
}

export const Route = createFileRoute('/dashboard/sports/$id/teams/$teamId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { id, teamId } = Route.useParams();
  const navigate = useNavigate();

  // Convert route id to lowercase for API (NFL -> nfl, NBA -> nba)
  const leagueId = id.toLowerCase();

  // TODO: This will come from DB when the functions have been sorted
  const { data: teams = [], isLoading: teamsLoading } = useGetFeedTeamsQuery(leagueId);
  const { data: feedPlayers = [], isLoading: playersLoading } = useGetFeedPlayersQuery(teamId);

  const team = useMemo(() => {
    return teams.find(t => t.externalId === teamId);
  }, [teams, teamId]);

  // Adapt FeedPlayer to PlayerData interface expected by TeamPlayersSection
  const players: PlayerData[] = useMemo(() => {
    return feedPlayers.map(player => ({
      id: player.externalId,
      teamId: player.teamId,
      name: player.displayName,
      position: player.position || '',
      jerseyNumber: player.jerseyNumber,
    }));
  }, [feedPlayers]);

  const sportName = id.toUpperCase();

  if (teamsLoading) {
    return (
      <PageContainer>
        <Breadcrumbs
          overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
          pathOverrides={{ sports: `/dashboard/sports/${id}` }}
        />
        <ContentCard>
          <div className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  if (!team) {
    return (
      <PageContainer>
        <Breadcrumbs
          overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
          pathOverrides={{ sports: `/dashboard/sports/${id}` }}
        />
        <ContentCard>
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">Team not found</p>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/dashboard/sports/$id/teams', params: { id } })}
              className="mt-4"
            >
              Back to Teams
            </Button>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ sports: 'Sports', [id]: id.toUpperCase() }}
        pathOverrides={{ sports: `/dashboard/sports/${id}` }}
      />
      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-500" />
          <h1 className="text-2xl font-semibold">
            {sportName} {'>'} Teams {'>'} {team.name}
          </h1>
        </div>

        <div className="flex gap-2 ml-auto">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/dashboard/sports/$id/teams', params: { id } })}
          >
            Cancel
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" disabled>
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <TeamInformationSection
          sportName={sportName}
          name={team.name}
          shortCode={team.abbreviation}
          feedId={team.externalId}
          logoUrl={
            team.abbreviation
              ? getTeamLogoUrl({ sport: sportName, teamShortcode: team.abbreviation })
              : undefined
          }
          avatarUrl={
            team.abbreviation
              ? (getTeamAvatarUrl(team.abbreviation, sportName as Sport) ?? undefined)
              : undefined
          }
          disabled
          canEditImages={isSuperAdmin()}
        />

        <TeamStatsSection sport={sportName} teamId={teamId} />

        <TeamPlayersSection
          teamName={team.name}
          sportName={sportName}
          players={players}
          isLoading={playersLoading}
          onEdit={playerId =>
            navigate({
              to: '/dashboard/sports/$id/players/$playerId',
              params: { id, playerId },
            })
          }
        />
      </div>
    </PageContainer>
  );
}
